import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { EmptyState } from '@pazportal/ui';
import { supabase } from '../lib/supabase';
import { formatDate } from '../lib/formatters';

interface LocationDocument {
  id: string;
  title: string;
  category: string | null;
  file_size_bytes: number | null;
  s3_key: string;
  external_url: string | null;
  source: string | null;
  is_customer_visible: boolean;
  metadata: unknown;
  created_at: string;
  updated_at: string;
}
const inputClass =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary';
const buttonClass =
  'min-h-11 rounded-md border border-border px-3 py-2 text-sm text-text hover:bg-surface-3 disabled:opacity-50';
const categories = [
  'other',
  'kb',
  'network_diagram',
  'rack_elevation',
  'floor_plan',
  'as_built',
  'photo',
  'contract',
  'certificate',
] as const;

export function LocationDocumentsPage() {
  const { id } = useParams<{ id: string }>();
  return id ? <LocationDocumentsWorkspace key={id} locationId={id} /> : null;
}

export function LocationDocumentsWorkspace({
  locationId,
}: {
  locationId: string;
}) {
  const client = useQueryClient();
  const [adding, setAdding] = useState<'file' | 'link' | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState<LocationDocument | null>(null);
  const queryKey = ['admin-location-documents', locationId];
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [location, documents] = await Promise.all([
        supabase
          .from('locations')
          .select('id,customer_id')
          .eq('id', locationId)
          .is('deleted_at', null)
          .single(),
        supabase
          .from('documents')
          .select(
            'id,title,category,file_size_bytes,s3_key,external_url,source,is_customer_visible,created_at,updated_at,metadata',
          )
          .eq('location_id', locationId)
          .order('created_at', { ascending: false }),
      ]);
      if (location.error) throw location.error;
      if (documents.error) throw documents.error;
      return {
        customerId: location.data.customer_id,
        documents: documents.data as LocationDocument[],
      };
    },
  });
  const invalidate = () => client.invalidateQueries({ queryKey });
  const publish = useMutation({
    mutationFn: async (doc: LocationDocument) => {
      const { error } = await supabase
        .from('documents')
        .update({ is_customer_visible: !doc.is_customer_visible })
        .eq('id', doc.id)
        .eq('location_id', locationId)
        .eq('is_customer_visible', doc.is_customer_visible)
        .eq('updated_at', doc.updated_at)
        .select('id')
        .single();
      if (error?.code === 'PGRST116')
        throw new Error(
          'This document changed after you reviewed it. Reload the page and review its latest contents before publishing.',
        );
      if (error) throw error;
    },
    onSuccess: async () => {
      setPublishing(null);
      await invalidate();
    },
  });
  const add = useMutation({
    mutationFn: async (form: FormData) => {
      if (!query.data) throw new Error('The site is not available.');
      const title = String(form.get('title') ?? '').trim();
      if (!title) throw new Error('Enter a document title.');
      let s3Key = `portal-links/${locationId}/${crypto.randomUUID()}`;
      let externalUrl: string | null = null;
      let file: File | null = null;
      if (adding === 'link') {
        const url = new URL(String(form.get('external_url') ?? '').trim());
        if (url.protocol !== 'https:' || url.username || url.password)
          throw new Error(
            'Use an HTTPS link without embedded sign-in credentials.',
          );
        externalUrl = url.href;
      } else {
        const upload = form.get('file');
        if (!(upload instanceof File) || upload.size === 0)
          throw new Error('Choose a file to upload.');
        if (upload.size > 50 * 1024 * 1024)
          throw new Error('The maximum document size is 50 MB.');
        file = upload;
        s3Key = `${query.data.customerId}/${locationId}/${crypto.randomUUID()}/${upload.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
        const { error } = await supabase.storage
          .from('documents')
          .upload(s3Key, upload, { upsert: false });
        if (error) throw error;
      }
      const { error } = await supabase.from('documents').insert({
        customer_id: query.data.customerId,
        location_id: locationId,
        title,
        category: String(
          form.get('category') ?? 'other',
        ) as (typeof categories)[number],
        s3_key: s3Key,
        external_url: externalUrl,
        file_size_bytes: file?.size ?? null,
        mime_type: file?.type || null,
        is_customer_visible: false,
      });
      if (error) {
        if (file) await supabase.storage.from('documents').remove([s3Key]);
        throw error;
      }
    },
    onSuccess: async () => {
      setAdding(null);
      await invalidate();
    },
  });

  async function open(doc: LocationDocument) {
    setOpenError(null);
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(doc.s3_key, 900);
      if (error) throw error;
      if (!data?.signedUrl)
        throw new Error('A download link could not be created.');
      const opened = window.open(data.signedUrl, '_blank', 'noopener');
      if (!opened) window.location.assign(data.signedUrl);
    } catch (error) {
      setOpenError(message(error));
    }
  }

  if (query.isLoading)
    return (
      <p role="status" className="text-sm text-text-muted">
        Loading documents…
      </p>
    );
  if (query.isError)
    return (
      <div role="alert">
        <EmptyState
          icon={<DocIcon />}
          title="Unable to load documents"
          subtitle={message(query.error)}
        />
        <button
          type="button"
          onClick={() => void query.refetch()}
          className={buttonClass}
        >
          Try again
        </button>
      </div>
    );
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-text sm:text-2xl">Documents</h1>
          <p className="mt-1 text-sm text-text-muted">
            Files and linked documentation for this site. New and migrated
            records stay internal until reviewed and published.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className={buttonClass}
            onClick={() => {
              add.reset();
              setAdding('file');
            }}
          >
            Upload file
          </button>
          <button
            type="button"
            className={buttonClass}
            onClick={() => {
              add.reset();
              setAdding('link');
            }}
          >
            Add document link
          </button>
        </div>
      </div>
      {adding && (
        <form
          key={adding}
          aria-label={
            adding === 'file' ? 'Upload document' : 'Add document link'
          }
          className="space-y-4 rounded-lg border border-border bg-surface p-4 sm:p-5"
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            add.mutate(new FormData(event.currentTarget));
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1 text-sm text-text-muted">
              Document title
              <input name="title" required className={inputClass} />
            </label>
            <label className="space-y-1 text-sm text-text-muted">
              Category
              <select name="category" className={inputClass}>
                {categories.map((category) => (
                  <option value={category} key={category}>
                    {category.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {adding === 'file' ? (
            <label className="block space-y-1 text-sm text-text-muted">
              File (up to 50 MB)
              <input type="file" name="file" required className={inputClass} />
            </label>
          ) : (
            <label className="block space-y-1 text-sm text-text-muted">
              HTTPS document URL
              <input
                type="url"
                name="external_url"
                required
                placeholder="https://"
                className={inputClass}
              />
            </label>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={add.isPending}
              className={`${buttonClass} bg-primary text-white`}
            >
              {add.isPending ? 'Saving…' : 'Save internal document'}
            </button>
            <button
              type="button"
              disabled={add.isPending}
              onClick={() => setAdding(null)}
              className={buttonClass}
            >
              Cancel
            </button>
          </div>
          {add.error && (
            <p role="alert" className="text-sm text-error">
              {message(add.error)}
            </p>
          )}
        </form>
      )}
      {openError && (
        <p role="alert" className="text-sm text-error">
          {openError}
        </p>
      )}
      {!query.data?.documents.length ? (
        <div className="rounded-lg border border-border bg-surface">
          <EmptyState
            icon={<DocIcon />}
            title="No documents"
            subtitle="Add a file or a link to start this site's documentation."
          />
        </div>
      ) : (
        <div className="divide-y divide-divider rounded-lg border border-border bg-surface">
          {query.data.documents.map((doc) => (
            <article
              key={doc.id}
              className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5"
            >
              <div className="min-w-0">
                <h2 className="break-words text-sm font-medium text-text">
                  {doc.title}
                </h2>
                <p className="mt-1 text-xs text-text-muted">
                  {(doc.category ?? 'other').replace(/_/g, ' ')} ·{' '}
                  {formatDate(doc.created_at)} ·{' '}
                  {doc.is_customer_visible
                    ? 'Published to customer'
                    : 'Internal'}
                  {doc.source ? ` · ${doc.source.replace(/_/g, ' ')}` : ''}
                  {doc.external_url ? ' · External document' : ''}
                </p>
                {documentDescription(doc) && (
                  <p className="mt-2 whitespace-pre-wrap text-sm text-text-muted">
                    {documentDescription(doc)}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {doc.external_url ? (
                  <a
                    href={doc.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${buttonClass} text-primary`}
                  >
                    Open document ↗
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => void open(doc)}
                    className={`${buttonClass} text-primary`}
                  >
                    Download
                  </button>
                )}
                <button
                  type="button"
                  className={buttonClass}
                  onClick={() => {
                    publish.reset();
                    setPublishing(doc);
                  }}
                >
                  {doc.is_customer_visible
                    ? 'Make internal'
                    : 'Review and publish'}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
      {publishing && (
        <section
          role="alertdialog"
          aria-labelledby="publish-title"
          aria-describedby="publish-description"
          className="rounded-lg border border-primary bg-surface p-5"
        >
          <h2 id="publish-title" className="font-semibold text-text">
            {publishing.is_customer_visible
              ? 'Remove customer visibility?'
              : 'Publish this document to the customer?'}
          </h2>
          <p id="publish-description" className="mt-2 text-sm text-text-muted">
            {publishing.title} —{' '}
            {publishing.is_customer_visible
              ? 'It will remain in the internal site documentation.'
              : 'Confirm that its contents and any linked resource are suitable for this customer. External resources retain their own access settings.'}
          </p>
          {documentDescription(publishing) && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-text-muted">
              {documentDescription(publishing)}
            </p>
          )}
          {publishing.external_url && (
            <a
              href={publishing.external_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 block break-all text-sm text-primary underline"
            >
              Inspect linked document ↗
            </a>
          )}
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              className={`${buttonClass} bg-primary text-white`}
              disabled={publish.isPending}
              onClick={() => publish.mutate(publishing)}
            >
              {publishing.is_customer_visible
                ? 'Make internal'
                : 'Publish to customer'}
            </button>
            <button
              type="button"
              className={buttonClass}
              disabled={publish.isPending}
              onClick={() => setPublishing(null)}
            >
              Cancel
            </button>
          </div>
          {publish.error && (
            <p role="alert" className="mt-2 text-sm text-error">
              {message(publish.error)}
            </p>
          )}
        </section>
      )}
    </div>
  );
}

function message(error: unknown) {
  return error && typeof error === 'object' && 'message' in error
    ? String(error.message)
    : 'The operation could not be completed.';
}
function documentDescription(doc: LocationDocument): string | null {
  return doc.metadata &&
    typeof doc.metadata === 'object' &&
    'mission_control_description' in doc.metadata &&
    typeof doc.metadata.mission_control_description === 'string'
    ? doc.metadata.mission_control_description
    : null;
}
function DocIcon() {
  return (
    <svg
      className="h-12 w-12"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}
